import { Chip, ListItem, ListItemText, Stack, styled } from '@ethings/ui-toolkit';
import { Autocomplete, TextField } from '@mui/material';
import type { JSX } from 'react';
import { CommonEntity } from '../../types';
import { Icon } from '../icons';

interface Props<T extends CommonEntity> {
  'data-testid'?: string;
  disabled?: boolean;
  label: string;
  onOptionDelete?: (id: string) => Promise<void> | void;
  onOptionSelect?: (id: string) => Promise<void> | void;
  options: T[];
  placeholder?: string;
  size?: 'small' | 'medium';
  tags?: T[];
  disablePortal?: boolean;
}

const StyledChip = styled(Chip)({
  margin: '0.2rem',
});

export const CommonEntityTagsEditor = <T extends CommonEntity>({
  disabled = false,
  label,
  onOptionDelete,
  onOptionSelect,
  options,
  placeholder,
  size = 'small',
  tags,
  disablePortal = false,
  ...props
}: Props<T>): JSX.Element => (
  <Stack data-testid={props['data-testid']} spacing={3} width="inherit">
    <Autocomplete
      disableClearable
      disabled={disabled}
      filterSelectedOptions
      getOptionLabel={({ display }) => display}
      multiple
      onChange={(_, commonEntities) => onOptionSelect?.(commonEntities[commonEntities.length - 1]._id)}
      options={options}
      popupIcon={<></>}
      renderInput={(params) => (
        <TextField
          {...params}
          data-testid="tags-editor"
          label={label}
          placeholder={placeholder}
          sx={{ input: { minWidth: '3em !important' } }}
          variant="outlined"
        />
      )}
      renderOption={(defaultProps, { _id, display }) => {
        return (
          <ListItem
            {...defaultProps}
            key={_id}
            onClick={() => onOptionSelect?.(_id)}
          >
            <ListItemText primary={display} />
          </ListItem>
        );
      }}
      renderTags={(commonEntities: readonly T[]) =>
        commonEntities.map(({ _id, display }) => (
          <StyledChip
            key={_id}
            deleteIcon={<Icon data-testid="delete-icon" icon="CircleX" />}
            disabled={disabled}
            label={display}
            onDelete={() => onOptionDelete?.(_id)}
            size={size}
          />
        ))
      }
      size={size}
      value={tags}
      disablePortal={disablePortal}
    />
  </Stack>
);
